import React, { useEffect, useState } from "react";
import { Button, Checkbox, Form, Input, Popconfirm, Select, Table, Tag, message } from "antd";
import dayjs from "dayjs";
import NutritionDrawer, { DrawerFooter } from "@components/nutrition/nutrition-drawer";
import {
  blockSprCard,
  deleteSprCard,
  fetchSprCardHistory,
  unblockSprCard,
  updateSprCard
} from "./spr-cards-api";
import { DOC_STATUS_LABELS } from "@core/card-keys";
import "./spr-cards.css";

const SprCardDrawer = ({ card, vids, open, onClose, onSaved, canEdit }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const isBlocked = Number(card?.isDel) === 1;
  const isFree = !isBlocked && !Number(card?.idDocExp) && !Number(card?.idClient);

  useEffect(() => {
    if (!open || !card) return;
    form.setFieldsValue({
      vid: card.vid || undefined,
      keyCardShort: card.keyCardShort || "",
      keyName: card.keyName || ""
    });
    setHistory([]);
    fetchSprCardHistory(card.idCard)
      .then((data) => setHistory(Array.isArray(data) ? data : []))
      .catch((e) => messageApi.error(e?.message || "Не удалось загрузить историю"));
  }, [open, card, form, messageApi]);

  const run = async (task, ok) => {
    setSaving(true);
    try {
      await task();
      messageApi.success(ok);
      onSaved?.();
      onClose();
    } catch (e) {
      messageApi.error(e?.message || "Операция не выполнена");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const values = await form.validateFields();
    await run(
      () => updateSprCard(card.idCard, {
        vid: values.vid || 0,
        keyCardShort: values.keyCardShort || "",
        keyName: values.keyName || ""
      }),
      "Карта сохранена"
    );
  };

  return (
    <NutritionDrawer
      open={open}
      title={card ? `Карта ${card.keyCard}` : "Карта"}
      width={480}
      onClose={onClose}
    >
      {contextHolder}
      <Form form={form} layout="vertical" disabled={!canEdit}>
        <Form.Item name="vid" label="Тип карты">
          <Select options={(vids || []).map((x) => ({ value: x.vid, label: x.name }))} allowClear />
        </Form.Item>
        <Form.Item name="keyCardShort" label="Короткий номер">
          <Input maxLength={80} />
        </Form.Item>
        <Form.Item name="keyName" label="Имя на карте">
          <Input maxLength={80} />
        </Form.Item>
      </Form>
      <div className="spr-cards-drawer-section">
        <div className="spr-cards-drawer-subtitle">История выдач</div>
        <Table
          rowKey={(r) => r.idStr}
          dataSource={history}
          pagination={false}
          size="small"
          locale={{ emptyText: "Выдач не было" }}
          columns={[
            {
              title: "Дата",
              width: 140,
              render: (_, r) => {
                if (r.fdatetime && dayjs(r.fdatetime).isValid()) return dayjs(r.fdatetime).format("DD.MM.YYYY HH:mm");
                return r.fdate && dayjs(r.fdate).isValid() ? dayjs(r.fdate).format("DD.MM.YYYY") : "—";
              }
            },
            {
              title: "Событие",
              width: 110,
              render: (_, r) => r.vid === 1
                ? <Tag color="green">Выдана</Tag>
                : r.vid === -1 ? <Tag>Возвращена</Tag> : <Tag>{r.vid}</Tag>
            },
            { title: "Владелец", render: (_, r) => r.holderName || "—" },
            { title: "Кто", width: 110, render: (_, r) => r.userName || "—" }
          ]}
        />
      </div>
      {canEdit ? (
        <div className="spr-cards-drawer-actions">
          {isBlocked ? (
            <Button loading={saving} onClick={() => run(() => unblockSprCard(card.idCard), "Карта разблокирована")}>
              Разблокировать
            </Button>
          ) : (
            <Button loading={saving} onClick={() => run(() => blockSprCard(card.idCard), "Карта заблокирована")}>
              Заблокировать
            </Button>
          )}
          {isFree ? (
            <Popconfirm title="Удалить карту?" onConfirm={() => run(() => deleteSprCard(card.idCard), "Карта удалена")}>
              <Button danger loading={saving}>Удалить</Button>
            </Popconfirm>
          ) : null}
        </div>
      ) : null}
      <DrawerFooter onSave={canEdit ? save : null} onClose={onClose} saving={saving} />
    </NutritionDrawer>
  );
};

export default SprCardDrawer;
